import dynamic from "next/dynamic";
import { Image, type Props as ImageProps } from "~/features/ui/image";
import { InlineVideo, type Props as InlineVideoProps } from "~/features/ui/video/inline-video";
import { ExternalVideo, type Props as ExternalVideoProps } from "~/features/ui/video/external-video";
import type { Props as AudioProps } from "~/features/ui/audio";
import { parsePalette } from "~/features/sanity/media/image-url";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { type AssetFragment } from "~/gql/graphql";
import { type ExcludeMembers, __IS_DEV__ } from "~/features/app/utils";
import { VideoProvider } from "~/features/ui/video/video-context";
import { omitGraphQLMetaProps } from "~/features/ui/dom-props";
const Audio = dynamic(() => import("~/features/ui/audio").then(m => ({
  default: m.Audio
})), {
  ssr: false
});
export const fragment = graphql(`
  fragment Asset on default_Asset {
    type: mimeType
    src: url
    alt
    oriWidth: width
    oriHeight: height
    palette
    focalPoint
    mimeType
  }
`);
export type Props = {
  asset?: FragmentType<typeof fragment>;
  inlineVideoProps?: Pick<InlineVideoProps, Exclude<keyof InlineVideoProps, Exclude<keyof AssetFragment, "src">>>;
  externalVideoProps?: ExcludeMembers<ExternalVideoProps, AssetFragment>;
  imageProps?: ExcludeMembers<Omit<ImageProps, "palette">, AssetFragment>;
  audioProps?: ExcludeMembers<AudioProps, AssetFragment>;
};
export function Asset(props: Props) {
  const {
    asset,
    imageProps,
    inlineVideoProps,
    externalVideoProps,
    audioProps,
    ...rest
  } = props;
  const {
    palette,
    type,
    ...fragmentRest
  } = useFragment(fragment, asset) ?? {};
  const cleanFragmentRest = omitGraphQLMetaProps(fragmentRest);
  if (type?.startsWith("image")) {
    return <Image {...rest} {...cleanFragmentRest} {...imageProps} palette={parsePalette(palette)} />;
  }
  if (type?.startsWith("video") || inlineVideoProps && "src" in inlineVideoProps) {
    return <VideoProvider>
        <InlineVideo {...rest} {...cleanFragmentRest} {...inlineVideoProps} />
      </VideoProvider>;
  }
  if (externalVideoProps && "videoId" in externalVideoProps) {
    return <VideoProvider>
        <ExternalVideo {...rest} {...cleanFragmentRest} {...externalVideoProps} />
      </VideoProvider>;
  }
  if (type?.startsWith("audio")) {
    return <Audio {...rest} {...cleanFragmentRest} {...audioProps} />;
  }
  return null;
}